import { useEffect, useId, useRef, useState } from "react";
import { Prompt } from "../components/Audio";
import { CheckIcon } from "../components/Icons";
import { AnswerField, MathKeypad, expressionKeysFor } from "../components/MathKeys";
import ui from "../components/ui.module.css";
import useSentBack from "../hooks/useSentBack";
import {
  formatExpression,
  isExpressionValue,
  readExpressionEntry,
  toDisplay,
} from "../data/maths/expression";


// Why an expression cannot be checked yet, in words a learner can act on.
export const expressionProblemText = (reason) =>
  ({
    empty: "Enter your answer first.",
    incomplete: "Your answer isn't finished yet.",
    malformed: "Check your brackets and signs.",
    symbol: "Use only the letters on the keypad.",
    operator: "Use only the keys on the keypad.",
    character: "Use only the keys on the keypad.",
    equation: "This answer is not an equation.",
  })[reason];

// What the keypad entry looks like in familiar notation, once it can be read.
export const Preview = ({ text, options }) => {
  const read = text ? readExpressionEntry(text, options) : null;
  return (
    <p className={ui.mathPreview} aria-hidden="true">
      {read?.tree ? toDisplay(read.tree) : " "}
    </p>
  );
};

// Enter an expression with a maths keypad and hand it in. Any readable
// expression is handed in; the gateway compares it with the explicitly
// authored accepted forms (syntax-only normalisation, not a computer algebra
// system, contract §4.3) and policy decides what happens next.
const ExpressionEntryStep = ({ step, saved, verdict, onDone, saving }) => {
  // The step's one expression contract, shared with validation and grading.
  const options = step.contract;
  const [text, setText] = useState(() =>
    isExpressionValue(saved?.expression) ? formatExpression(saved.expression, options) : ""
  );
  const [problem, setProblem] = useState(null);
  const [result, setResult] = useState(null);
  const timer = useRef(null);
  const messageId = useId();

  useEffect(() => () => clearTimeout(timer.current), []);

  useSentBack(verdict, () => setResult("wrong"));

  const edit = (next) => {
    setText(next);
    setProblem(null);
    setResult(null);
  };

  const check = () => {
    if (result === "sent" || saving) return;
    const read = readExpressionEntry(text, options);
    if (read.error) {
      setProblem(read.error);
      return;
    }
    setResult("sent");
    onDone({ expression: read.value });
  };

  const locked = saving || result === "sent";

  return (
    <>
      <Prompt step={step} />
      <div className={ui.numericAnswer}>
        <AnswerField
          value={text}
          onChange={edit}
          label="Your answer"
          invalid={Boolean(problem)}
          describedBy={messageId}
          disabled={locked}
        />
        <Preview text={text} options={options} />
        <MathKeypad
          value={text}
          onChange={edit}
          extraKeys={expressionKeysFor(step)}
          disabled={locked}
          label="Maths keys"
        />
        <p id={messageId} className={ui.mathProblem} role="alert">
          {problem ? expressionProblemText(problem) : ""}
        </p>
        <button
          type="button"
          className={`${ui.button} ${ui.large} ${ui.primary}`}
          onClick={check}
          disabled={locked}
        >
          <CheckIcon size={24} />
          {saving ? "Saving…" : "Done"}
        </button>
      </div>
    </>
  );
};

export default ExpressionEntryStep;
